<template>
  <div class="tx-records-page">
    <title-bar :title="$lang.txRecords_title || 'TRANSACTION RECORDS'" />

    <!-- Filter -->
    <div class="tx-filter">
      <van-dropdown-menu>
        <van-dropdown-item
          v-model="filterValue"
          :options="filterOptions"
          @change="onFilterChange"
        />
      </van-dropdown-menu>
    </div>

    <div v-if="recordReadError" class="tx-diagnostic">
      {{ recordReadError }}
    </div>

    <!-- Transaction List -->
    <van-list
      v-model="loading"
      :finished="finished"
      loading-text=" "
      @load="loadMore"
      class="tx-list"
    >
      <template v-for="(group, gIdx) in groupedList">
        <div class="tx-date-header" :key="'h' + gIdx">{{ group.date }}</div>
        <div
          v-for="(item, idx) in group.items"
          :key="'t' + gIdx + '-' + idx"
          class="tx-item"
          @click="goToDetail(item)"
        >
          <div class="tx-item__info">
            <p v-if="item.orderType === 'pay'" class="tx-item__type">{{ $lang.Recarregar || 'Deposit' }}</p>
            <p v-else class="tx-item__type">
              <span>{{ $lang.recordOrder_txt || 'Withdraw' }} {{ $lang.account || 'Account' }}</span><span v-if="withdrawAccountText(item)">({{ withdrawAccountText(item) }})</span>
            </p>
            <p class="tx-item__time">{{ item.createDate }}</p>
          </div>
          <div class="tx-item__right">
            <p
              class="tx-item__amount"
              :class="item.orderType === 'wd' ? 'tx-item__amount--red' : 'tx-item__amount--green'"
            >
              {{ item.orderType === 'wd' ? '-' : '+' }} {{ $formatNumberWithCommas(item.orderAmount) }}
            </p>
            <span
              class="tx-item__status"
              :class="getStatusClass(item.orderStatus)"
            >
              {{ item.orderStatusName }}
            </span>
          </div>
        </div>
      </template>

      <!-- 没有数据时显示 -->
      <van-empty
        v-if="!list.length && !loading"
        :image="require('../../../assets/img/common/img_no_data.png')"
        :description="$lang.noempt"
      />
    </van-list>
  </div>
</template>

<script>
import { GetRWHistory } from '@/api/common'
import {
  contentOf,
  failMessage,
  firstArray,
  handleNeedLogin,
  isFailedOrderStatus,
  isOk,
  isSuccessfulOrderStatus,
  normalizeOrderRecord
} from './safeFlow'
import { safeGetStorageItem, safeLocalStorage } from '@/utils/safeStorage'

function getLocalStorage() {
  try {
    if (typeof safeLocalStorage === 'function') return safeLocalStorage()
  } catch (e) { return null }
  return null
}

function fallbackToken(value) {
  return String(value == null ? '' : value).trim().toLowerCase()
}

function isOkStatusToken(value) {
  return ['ok', 'success', 'successful', 'succeed', 'succeeded'].includes(fallbackToken(value))
}

function isOkCodeToken(value) {
  return ['0', '00', '0000', '000000', '200'].includes(fallbackToken(value))
}

function hasOkEnvelopeMarker(res, content) {
  const source = content && typeof content === 'object' ? content : {}
  return [
    res && res.status,
    res && res.state,
    res && res.result,
    source.status,
    source.state,
    source.result
  ].some(isOkStatusToken) ||
    [
      res && res.code,
      res && res.errorCode,
      source.code,
      source.errorCode
    ].some(isOkCodeToken) ||
    [res && res.success, source.success].some(value => value === true || fallbackToken(value) === 'true')
}

function isActiveMarkerValue(value) {
  const token = fallbackToken(value)
  return value === true || value === 1 || ['true', '1', 'yes', 'on'].includes(token)
}

function hasServerReadProof(payload, depth = 0, seen = []) {
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return false
  seen.push(payload)
  if (Array.isArray(payload)) return payload.some(item => hasServerReadProof(item, depth + 1, seen))
  if (['serverReadProof', 'server_read_proof', 'readProof', 'read_proof'].some(key => isActiveMarkerValue(payload[key]))) return true
  return Object.keys(payload).some(key => {
    const value = payload[key]
    return value && typeof value === 'object' && hasServerReadProof(value, depth + 1, seen)
  })
}

function hasTransactionReadUnavailableMarker(payload, depth = 0, seen = []) {
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return false
  seen.push(payload)
  if (Array.isArray(payload)) return payload.some(item => hasTransactionReadUnavailableMarker(item, depth + 1, seen))
  const markerKeys = [
    'readOnly',
    'readOnlyAction',
    'readOnlyMode',
    'readonly',
    'read_only',
    'read_only_action',
    'read_only_mode',
    'mock',
    'mockMode',
    'mock_mode',
    'mockFallback',
    'mock_fallback',
    'fallback',
    'fallbackUsed',
    'fallback_used',
    'failClosed',
    'fail_closed',
    'falseProof',
    'false_proof',
    'placeholder',
    'placeholderMode',
    'placeholder_mode',
    'placeholderResponse',
    'placeholder_response',
    'dataUnavailable',
    'data_unavailable',
    'readUnavailable',
    'read_unavailable',
    'readFailure',
    'read_failure',
    'readFailed',
    'read_failed',
    'readBlocked',
    'read_blocked',
    'routeUnavailable',
    'route_unavailable',
    'technicalError',
    'technical_error',
    'technicalException',
    'technical_exception',
    'technicalMarker',
    'technical_marker',
    'backendFailure',
    'backend_failure',
    'unimplemented',
    'notImplemented',
    'not_implemented',
    'notSupported',
    'not_supported',
    'notConnected',
    'not_connected',
    'unsupported',
    'queryOnly',
    'query_only',
    'onlyQuery',
    'only_query',
    'blocked',
    'writeBlocked',
    'write_blocked',
    'backendBlocked',
    'backend_blocked',
    'paymentBackendBlocked',
    'payment_backend_blocked'
  ]
  return Object.keys(payload).some(key => {
    const value = payload[key]
    const keyToken = fallbackToken(key)
    const valueToken = fallbackToken(value)
    if (['readOnlyQuery', 'read_only_query'].includes(key) && isActiveMarkerValue(value) && !hasServerReadProof(payload)) return true
    if (markerKeys.includes(key) && isActiveMarkerValue(value)) return true
    if (value && typeof value === 'object') return hasTransactionReadUnavailableMarker(value, depth + 1, seen)
    return ['message', 'msg', 'reason', 'desc', 'description', 'code', 'errorcode'].includes(keyToken) && (
      valueToken.includes('read_only') ||
      valueToken.includes('read only') ||
      valueToken.includes('readonly') ||
      valueToken.includes('mock') ||
      valueToken.includes('fallback') ||
      valueToken.includes('failclosed') ||
      valueToken.includes('fail_closed') ||
      valueToken.includes('fail-closed') ||
      valueToken.includes('fail closed') ||
      valueToken.includes('falseproof') ||
      valueToken.includes('false_proof') ||
      valueToken.includes('false-proof') ||
      valueToken.includes('false proof') ||
      valueToken.includes('placeholder') ||
      valueToken.includes('data_unavailable') ||
      valueToken.includes('data unavailable') ||
      valueToken.includes('read_unavailable') ||
      valueToken.includes('read unavailable') ||
      valueToken.includes('read_blocked') ||
      valueToken.includes('read blocked') ||
      valueToken.includes('read_failure') ||
      valueToken.includes('read failure') ||
      valueToken.includes('read_failed') ||
      valueToken.includes('read failed') ||
      valueToken.includes('route_unavailable') ||
      valueToken.includes('route unavailable') ||
      valueToken.includes('technical_error') ||
      valueToken.includes('technical error') ||
      valueToken.includes('technical_exception') ||
      valueToken.includes('technical exception') ||
      valueToken.includes('technicalmarker') ||
      valueToken.includes('technical_marker') ||
      valueToken.includes('technical marker') ||
      valueToken.includes('technical-marker') ||
      valueToken.includes('backendfailure') ||
      valueToken.includes('backend_failure') ||
      valueToken.includes('backend failure') ||
      valueToken.includes('backend-failure') ||
      valueToken.includes('unimplemented') ||
      valueToken.includes('not_implemented') ||
      valueToken.includes('not implemented') ||
      valueToken.includes('unsupported') ||
      valueToken.includes('not_supported') ||
      valueToken.includes('not supported') ||
      valueToken.includes('not_connected') ||
      valueToken.includes('not connected') ||
      valueToken.includes('query_only') ||
      valueToken.includes('query only') ||
      valueToken.includes('blocked') ||
      valueToken.includes('payment_backend_blocked') ||
      valueToken.includes('payment backend blocked') ||
      valueToken.includes('只支持查询') ||
      valueToken.includes('仅支持查询') ||
      valueToken.includes('技术错误') ||
      valueToken.includes('技术异常')
    )
  })
}

function isTrustedTransactionRead(res, content, items) {
  if (isOk(res)) return !hasTransactionReadUnavailableMarker(res)
  return Array.isArray(items) &&
    !hasTransactionReadUnavailableMarker(res) &&
    hasOkEnvelopeMarker(res, content)
}

function transactionFailureMessage(error, fallback) {
  const message = failMessage(error, fallback)
  return /^network error$/i.test(String(message || '').trim()) ? fallback : message
}

function readDateRange(range) {
  if (typeof range === 'string' && range.includes('&')) {
    const parts = range.split('&')
    return [String(parts[0] || '').trim(), String(parts[1] || '').trim()]
  }
  if (Array.isArray(range)) {
    return [String(range[0] || '').trim(), String(range[1] || '').trim()]
  }
  const value = String(range || '')
  return [value.slice(0, 19).trim(), value.slice(20, 40).trim()]
}

export default {
  name: 'TransactionRecords',
  data() {
    return {
      list: [],
      loading: false,
      finished: false,
      page: 0,
      filterValue: 6,
      filterOptions: [
        { text: this.$lang.billGame_txt4 || 'Today', value: 0 },
        { text: this.$lang.billGame_txt5 || 'Yesterday', value: 1 },
        { text: this.$lang.billGame_txt6 || 'This Week', value: 2 },
        { text: this.$lang.billGame_txt7 || 'Last Week', value: 3 },
        { text: this.$lang.billGame_txt8 || 'This Month', value: 4 },
        { text: this.$lang.billGame_txt9 || 'Last Month', value: 5 },
        { text: this.$lang.billGame_txt10 || 'All', value: 6 }
      ],
      params: {},
      pageSize: 20,
      recordReadError: '',
      replaceListOnSuccess: false
    }
  },
  computed: {
    groupedList() {
      const groups = {}
      this.list.forEach(item => {
        const parsed = item.createDate ? this.$dayjs(item.createDate) : null
        const date = parsed && parsed.isValid() ? parsed.format('YYYY-MM-DD') : '--'
        if (!groups[date]) groups[date] = { date, items: [] }
        groups[date].items.push(item)
      })
      return Object.values(groups)
    }
  },
  mounted() {
    if (!this.ensureLogin()) this.finished = true
  },
  methods: {
    ensureLogin() {
      if (safeGetStorageItem(getLocalStorage(), 'token')) return true
      if (this.$bus) this.$bus.$emit('openLogin')
      this.$jumpTo('/home', {}, { replace: true })
      return false
    },
    formatCardNumber(cardNumber) {
      cardNumber = String(cardNumber || '')
      const length = cardNumber.length
      if (length <= 4) return cardNumber
      return '***' + cardNumber.slice(-4)
    },
    withdrawAccountText(item) {
      const parts = [item.bankName, this.formatCardNumber(item.bankCard)].filter(Boolean)
      return parts.join(' ')
    },
    goToDetail(item) {
      if (!this.ensureLogin()) return
      this.$jumpTo('/orderDetail', { obj: item })
    },
    getStatusClass(status) {
      if (isSuccessfulOrderStatus(status))
        return 'tx-item__status--success'
      if (isFailedOrderStatus(status))
        return 'tx-item__status--failed'
      return 'tx-item__status--pending'
    },
    onFilterChange(i) {
      if (!this.ensureLogin()) return
      this.resetList(true)
      if (i === 0) {
        const [startDate, endDate] = readDateRange(this.$getTodayRange())
        this.fetchData(startDate, endDate)
      } else if (i === 1) {
        const [startDate, endDate] = readDateRange(this.$getYesterdayRange())
        this.fetchData(startDate, endDate)
      } else if (i === 2) {
        const [startDate, endDate] = readDateRange(this.$getThisWeekRange())
        this.fetchData(startDate, endDate)
      } else if (i === 3) {
        const [startDate, endDate] = readDateRange(this.$getLastWeekRange())
        this.fetchData(startDate, endDate)
      } else if (i === 4) {
        const [startDate, endDate] = readDateRange(this.$getThisMonthRange())
        this.fetchData(startDate, endDate)
      } else if (i === 5) {
        const [startDate, endDate] = readDateRange(this.$getLastMonthRange())
        this.fetchData(startDate, endDate)
      } else if (i === 6) {
        this.fetchData('', '')
      }
    },
    resetList(preserveCurrentList = false) {
      if (!preserveCurrentList) this.list = []
      this.page = 0
      this.finished = false
      this.replaceListOnSuccess = preserveCurrentList
    },
    transactionRecordKey(item) {
      const record = item || {}
      const parts = [
        record.orderNo || record.orderId || record.id || record.recordId,
        record.orderType || record.type,
        record.createDate || record.createdAt || record.time,
        record.orderAmount || record.amount,
        record.orderStatus || record.status
      ].map(value => value === undefined || value === null ? '' : String(value))
      return parts.some(Boolean) ? parts.join('|') : ''
    },
    mergeTransactionList(items) {
      const seen = new Set(this.list.map(item => this.transactionRecordKey(item)).filter(Boolean))
      const rows = Array.isArray(items) ? items : []
      const next = rows.filter(item => {
        const key = this.transactionRecordKey(item)
        if (!key) return true
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      this.list = this.list.concat(next)
    },
    async fetchData(startDate, endDate) {
      if (!this.ensureLogin()) {
        this.finished = true
        return
      }
      if (this.finished && this.page > 0) return
      this.params = {
        startDate: startDate,
        endDate: endDate,
        pageIndex: this.page,
        pageSize: this.pageSize
      }
      this.loading = true
      try {
        const data = await GetRWHistory(this.params)
        if (handleNeedLogin(this, data, this.$lang.login_txt11 || this.$lang.Entrar || 'Login Now', { isValue: 'isLogin' }, { replace: true })) {
          this.finished = true
          return
        }
        const content = contentOf(data)
        let items = firstArray(content, ['orderRecordList', 'dataList', 'list', 'records', 'items', 'pageList', 'capitalDetailsList'])
        if (isTrustedTransactionRead(data, content, items)) {
          this.recordReadError = ''
          items = items.map(item => normalizeOrderRecord(item, this.$lang))
          if (this.replaceListOnSuccess && this.page === 0) {
            this.list = []
          }
          this.replaceListOnSuccess = false
          if (items.length < this.pageSize) {
            this.finished = true
          }
          this.mergeTransactionList(items)
          this.page++
        } else {
          this.recordReadError = failMessage(data, 'This feature is temporarily unavailable. Please try again later.')
          this.$toast({
            message: this.recordReadError,
            icon: 'cross'
          })
          this.finished = true
        }
      } catch (e) {
        this.recordReadError = transactionFailureMessage(e, '当前交易记录读取失败，已保留已加载记录。')
        this.$toast({ message: this.recordReadError, icon: 'cross' })
        this.finished = true
      } finally {
        this.loading = false
      }
    },
    loadMore() {
      const { startDate, endDate } = this.params
      this.fetchData(startDate || '', endDate || '')
    }
  }
}
</script>

<style lang="less" scoped>
.tx-records-page {
  min-height: 100vh;
  background: #1a1d29;
}

/* ===== Filter ===== */
.tx-filter {
  position: sticky;
  top: 46px;
  z-index: 10;
  padding: 10px 16px;
}

::v-deep .van-dropdown-menu__bar {
  background: #252936;
  height: 30px !important;
  border-radius: 20px;
  border: none;
  padding: 0 18px;
  box-shadow: none;

  .van-dropdown-menu__title {
    color: #ffffff !important;
  }
  .van-dropdown-menu__title--active {
    color: #ff5a1f !important;
  }
}

::v-deep .van-dropdown-menu__title {
  font-size: var(--vk-fs-xs) !important;
  .van-ellipsis {
    margin-right: 5px;
  }
}

::v-deep .van-dropdown-item__content {
  font-size: var(--vk-fs-xs) !important;
  .van-cell__value {
    display: none;
  }
}

::v-deep .van-dropdown-item {
  width: 145px;
  border-radius: 15px;
  margin-top: 5px;
  margin-left: 3%;
  border: 1px solid rgba(255, 255, 255, 0.15);
  height: 240px;

  .van-dropdown-item__content {
    max-height: 100%;
  }
  .van-overlay {
    background-color: transparent !important;
  }
  .van-cell {
    background-color: #252a3d;
    color: #ffffff;
    padding: 5px 16px;
  }
}

::v-deep .van-dropdown-item__option--active {
  color: #ff5a1f !important;
  font-weight: bold;
}

::v-deep .van-dropdown-item__option--active .van-dropdown-item__icon {
  color: #ff5a1f !important;
}

::v-deep .van-loading {
  background: transparent;
}

.tx-diagnostic {
  margin: 0 16px 8px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(245, 158, 11, 0.35);
  background: rgba(245, 158, 11, 0.12);
  color: #fef3c7;
  font-size: var(--vk-fs-xs);
  line-height: 18px;
}

/* ===== Transaction List ===== */
.tx-list {
  height: calc(100vh - 100px);
  overflow-y: auto;
  padding: 0 0 20px;
}

/* ===== Date Header ===== */
.tx-date-header {
  position: sticky;
  top: 0;
  z-index: 5;
  padding: 12px 16px;
  font-size: var(--vk-fs-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--vk-t2);
  background: rgba(26, 29, 41, 0.9);
  backdrop-filter: blur(8px);
}

/* ===== Transaction Item ===== */
.tx-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
}

/* Info */
.tx-item__info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.tx-item__type {
  font-size: var(--vk-fs-base);
  font-weight: 700;
  color: #ffffff;
  margin: 0;
}

.tx-item__time {
  font-size: var(--vk-fs-xs);
  color: var(--vk-t3);
  margin: 4px 0 0;
}

/* Right */
.tx-item__right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
}

.tx-item__amount {
  font-size: var(--vk-fs-15);
  font-weight: 700;
  margin: 0;
}

.tx-item__amount--green {
  color: #15c26b;
}

.tx-item__amount--red {
  color: var(--vk-danger);
}

/* Status Badge */
.tx-item__status {
  font-size: var(--vk-fs-2xs);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 4px;
}

.tx-item__status--success {
  color: #15c26b;
}

.tx-item__status--pending {
  color: #f8f510;
}

.tx-item__status--failed {
  color: var(--vk-danger);
}

/* ===== PC Responsive ===== */
@media (min-width: 769px) {
  ::v-deep .van-dropdown-item {
    left: 50% !important;
    transform: translate(-50%, 0%) !important;
    margin-left: -142px !important;
  }
}
</style>
